<script lang="ts" setup>
/**
 * UiHeading — Renders a WCAG-compliant heading tag based on the component tree.
 *
 * Level is determined purely by provide/inject — no side effects, no mutations.
 * - force-tag="div" for explicit tags
 * - Header/Footer inject 'div' to suppress heading semantics
 * - <UiHeadingSection> increases the level for descendants
 *
 * @prop forceTag - Override with a specific tag (e.g., 'h1', 'h3', 'div').
 *                  Non-heading tags get role="heading" + aria-level for accessibility.
 */
const { forceTag } = defineProps<{
  forceTag?: string
}>()

const { public: config } = useRuntimeConfig()

const injectTag = inject('ui-heading-seo-level', undefined)
const injectLevel = inject('ui-heading-wcag-level', undefined)

const { seoLevel, wcagLevel } = useUiHeading()

// Priority: forceTag > injectTag (header/footer 'div') > auto level
const tag = forceTag || (config?.seoHeadingStrategy === 'manual' ? 'div' : undefined) || injectTag || levelToTag(seoLevel)

// ARIA: non-heading elements need role="heading" + aria-level
const headingLevel = (injectLevel !== undefined ? injectLevel : wcagLevel) || undefined
</script>

<template>
  <component
    :is="tag"
    class="heading"
    :role="headingLevel ? 'heading' : undefined"
    :aria-level="headingLevel"
  >
    <slot />
  </component>
</template>
